import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ============ Animation Keyframes ============
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const progressAnimation = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

// ============ Styled Components ============
const ToastContainer = styled.div<{ $isExiting: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  padding: 16px 20px;
  min-width: 320px;
  max-width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05);
  animation: ${props => props.$isExiting ? css`${slideOut} 0.3s ease-in forwards` : css`${slideIn} 0.4s ease-out`};
`;

const ToastHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const SuccessIcon = styled.div`
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const SuccessTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #22c55e;
  letter-spacing: 0.5px;
`;

const SwapDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
`;

const TokenAmount = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TokenIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #333;
`;

const TokenFallbackIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: white;
`;

const AmountText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const SymbolText = styled.span`
  font-size: 13px;
  color: #888;
  font-weight: 500;
`;

const ArrowIcon = styled.div`
  color: #666;
  font-size: 16px;
  margin: 0 4px;
`;

const LinkContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const TransactionLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #818cf8;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  
  &:hover {
    color: #999;
  }
`;

const ProgressBar = styled.div<{ $duration: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
  border-radius: 0 0 12px 12px;
  animation: ${progressAnimation} ${props => props.$duration}ms linear forwards;
`;

// ============ Types ============
export interface SwapToastData {
  fromAmount: string;
  fromSymbol: string;
  fromLogo?: string;
  toAmount: string;
  toSymbol: string;
  toLogo?: string;
  txHash?: string;
  requestId?: string;
  originChainId?: number;
}

interface ToastProps {
  data: SwapToastData;
  onClose: () => void;
  duration?: number;
}

// ============ Constants ============
const SOLANA_CHAIN_ID = 792703809;

// ============ Chain Explorer URLs ============
const CHAIN_EXPLORERS: Record<number, { url: string; txPath: string }> = {
  57073: { url: 'https://explorer.inkonchain.com', txPath: '/tx/' },
  1: { url: 'https://etherscan.io', txPath: '/tx/' },
  8453: { url: 'https://basescan.org', txPath: '/tx/' },
  10: { url: 'https://optimistic.etherscan.io', txPath: '/tx/' },
  42161: { url: 'https://arbiscan.io', txPath: '/tx/' },
  137: { url: 'https://polygonscan.com', txPath: '/tx/' },
  56: { url: 'https://bscscan.com', txPath: '/tx/' },
  43114: { url: 'https://snowtrace.io', txPath: '/tx/' },
  792703809: { url: 'https://solscan.io', txPath: '/tx/' }, // Solana uses /tx/ not /tx/
  143: { url: 'https://monadexplorer.com', txPath: '/tx/' },
};

// ============ Toast Component ============
export const SwapSuccessToast: React.FC<ToastProps> = ({ 
  data, 
  onClose, 
  duration = 8000 
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 300);

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  };

  // Build Relay transaction URL
  // For cross-chain swaps, Relay uses the format: /transaction/{chainId}/{signature}
  const isSolanaOrigin = data.originChainId === SOLANA_CHAIN_ID;
  const relayUrl = (() => {
    if (data.requestId) {
      // If we have a requestId, use it directly
      return `https://relay.link/transaction/${data.requestId}`;
    }
    if (data.txHash) {
      // For Solana, include chain ID in the URL
      if (isSolanaOrigin) {
        return `https://relay.link/transaction/${SOLANA_CHAIN_ID}/${data.txHash}`;
      }
      // For EVM chains, use tx hash directly
      return `https://relay.link/transaction/${data.txHash}`;
    }
    return null;
  })();

  // Build block explorer URL for origin chain
  const explorerUrl = (() => {
    if (!data.txHash || !data.originChainId) return null;
    const explorer = CHAIN_EXPLORERS[data.originChainId] || CHAIN_EXPLORERS[1];
    return `${explorer.url}${explorer.txPath}${data.txHash}`;
  })();

  return (
    <ToastContainer $isExiting={isExiting}>
      <CloseButton onClick={handleClose}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </CloseButton>

      <ToastHeader>
        <SuccessIcon>✓</SuccessIcon>
        <SuccessTitle>Swap Successful!</SuccessTitle>
      </ToastHeader>

      <SwapDetails>
        <TokenAmount>
          {data.fromLogo ? (
            <TokenIcon src={data.fromLogo} alt={data.fromSymbol} onError={(e) => e.currentTarget.style.display = 'none'} />
          ) : (
            <TokenFallbackIcon>{data.fromSymbol?.charAt(0) || '?'}</TokenFallbackIcon>
          )}
          <AmountText>{parseFloat(data.fromAmount).toFixed(6)}</AmountText>
          <SymbolText>{data.fromSymbol}</SymbolText>
        </TokenAmount>

        <ArrowIcon>→</ArrowIcon>

        <TokenAmount>
          {data.toLogo ? (
            <TokenIcon src={data.toLogo} alt={data.toSymbol} onError={(e) => e.currentTarget.style.display = 'none'} />
          ) : (
            <TokenFallbackIcon>{data.toSymbol?.charAt(0) || '?'}</TokenFallbackIcon>
          )}
          <AmountText>{parseFloat(data.toAmount).toFixed(6)}</AmountText>
          <SymbolText>{data.toSymbol}</SymbolText>
        </TokenAmount>
      </SwapDetails>

      <LinkContainer>
        {relayUrl && (
          <TransactionLink href={relayUrl} target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
            </svg>
            View on Relay
          </TransactionLink>
        )}
        {explorerUrl && (
          <TransactionLink href={explorerUrl} target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
            </svg>
            {isSolanaOrigin ? 'Solscan' : 'Explorer'}
          </TransactionLink>
        )}
      </LinkContainer>

      <ProgressBar $duration={duration} />
    </ToastContainer>
  );
};

// ============ Toast Container for Multiple Toasts ============
interface ToastItem extends SwapToastData {
  id: string;
}

interface ToastManagerProps {
  toasts: ToastItem[];
  onRemove: (id: string) => void;
}

export const ToastManager: React.FC<ToastManagerProps> = ({ toasts, onRemove }) => {
  return (
    <>
      {toasts.map((toast, index) => (
        <div 
          key={toast.id} 
          style={{ 
            position: 'fixed', 
            top: `${20 + index * 180}px`, 
            right: '20px',
            zIndex: 9999 - index 
          }}
        >
          <SwapSuccessToast
            data={toast}
            onClose={() => onRemove(toast.id)}
          />
        </div>
      ))}
    </>
  );
};

export default SwapSuccessToast;

